import React from 'react';
import { Test } from '../../types/test';

interface TestCardProps {
  test: Test;
  onRunBasic: () => void;
  onRunAuto: () => void;
  onRunWithCookies: () => void;
  onDelete: () => void;
}

const TestCard: React.FC<TestCardProps> = ({ test, onRunBasic, onRunAuto, onRunWithCookies, onDelete }) => {
  const getStatusColor = (status: Test['status']) => {
    switch (status) {
      case 'completed':
        return 'text-green-400 bg-green-400/10';
      case 'running':
        return 'text-yellow-400 bg-yellow-400/10 animate-pulse';
      case 'failed':
        return 'text-red-400 bg-red-400/10';
      default:
        return 'text-zinc-400 bg-zinc-400/10';
    }
  };

  const getTestResultColor = (result: Test['test_result']) => {
    switch (result) {
      case 'pass':
        return 'text-green-400 bg-green-400/10';
      case 'fail':
        return 'text-red-400 bg-red-400/10';
      default:
        return 'text-zinc-400 bg-zinc-400/10';
    }
  };

  const getStatusIcon = (status: Test['status']) => {
    switch (status) {
      case 'completed':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'running':
        return (
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'failed':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-lg p-6 hover:border-zinc-700 transition-all duration-200">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-3">
            <h3 className="text-xl font-semibold text-zinc-100">{test.name}</h3>
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(test.status)}`}>
              {getStatusIcon(test.status)}
              {test.status}
            </span>
            {test.status === 'completed' && test.test_result !== 'NA' && (
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getTestResultColor(test.test_result)}`}>
                {test.test_result === 'pass' ? '✓' : '✗'} {test.test_result.toUpperCase()}
              </span>
            )}
          </div>
          
          <p className="text-zinc-400 text-sm mb-3 line-clamp-2">{test.task}</p>
          
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
              </svg>
              <span className="font-mono">{test.session_id}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Last run: {formatDate(test.last_run)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 ml-6">
          <button
            onClick={onRunBasic}
            disabled={test.status === 'running'}
            className="px-4 py-2 bg-blue-800 hover:bg-blue-700 rounded-lg font-medium text-sm text-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            BASIC
          </button>

          <button
            onClick={onRunAuto}
            disabled={test.status === 'running'}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg font-medium text-sm text-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            AUTO
          </button>
          
          <button
            onClick={onRunWithCookies}
            disabled={test.status === 'running'}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg font-medium text-sm text-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            PROVIDE COOKIES
          </button>

          <button
            onClick={() => {
              if (window.confirm(`Are you sure you want to delete the test "${test.name}"? This action cannot be undone.`)) {
                onDelete();
              }
            }}
            className="px-4 py-2 bg-red-800 hover:bg-red-700 rounded-lg font-medium text-sm text-red-200 transition-all duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            DELETE
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestCard;